// src/features/tag-types/components/TagTypeList.tsx

import React from 'react';
import { List, TextField, FunctionField, useTranslate, DateField } from 'react-admin';
import { Typography, Chip, Tooltip } from '@mui/material';
import { CheckCircle, Cancel } from '@mui/icons-material';
import CustomDataGrid from '../../../components/common/CustomDatagrid';
import TagTypeActions from './TagTypeActions';
import { useTagTypePermissions } from '../hooks/useTagTypePermissions';
import { TagType } from '../types';
import CustomListToolbar from '../../../components/common/CustomListToolbar';
import { CustomTextInputFilter } from '@/components/common/CustomFilters';

export const TagTypeList: React.FC = (props) => {
  const translate = useTranslate();
  const { canManage, canCreate } = useTagTypePermissions();

  if (!canManage) {
    return (
      <Typography color="error">
        {translate('resources.tag-types.messages.access_denied', 'Acceso Denegado. Solo SuperAdmins pueden gestionar los tipos de ID.')}
      </Typography>
    );
  }

  return (
    <List
      {...props}
      title={translate('resources.tag-types.titles.list', 'Tipos de ID')}
      actions={
        <CustomListToolbar
          showFilterButton={false}
          showCreate={canCreate}
          createLabel={translate('resources.tag-types.actions.create', 'Nuevo Tipo de ID')}
        />
      }
      filters={[
        <CustomTextInputFilter 
          key="search" 
          source="q" 
          label={translate('resources.tag-types.fields.search_placeholder', 'Buscar tipos de ID...')} 
          alwaysOn 
        />
      ]}
      perPage={25}
      sort={{ field: 'name', order: 'ASC' }}
    >
      <CustomDataGrid rowClick="edit">
        {/* Columna de acciones */}
        <FunctionField
          label=""
          render={(record: TagType) => <TagTypeActions record={record} />}
          sortable={false}
        />
        
        {/* Type Code */}
        <FunctionField
          source="typeCode"
          label={translate('resources.tag-types.fields.typeCode', 'Código de Tipo')}
          render={(record: TagType) => (
            <Chip 
              label={record.typeCode} 
              size="small" 
              variant="outlined" 
              color="secondary" 
            />
          )}
        />
        
        {/* Name */}
        <TextField 
          source="name" 
          label={translate('resources.tag-types.fields.name', 'Nombre')} 
        />
        
        {/* Description */}
        <TextField 
          source="description" 
          label={translate('resources.tag-types.fields.description', 'Descripción')} 
        />
        
        {/* System Generated */}
        <FunctionField
          label={translate('resources.tag-types.fields.isSystemGenerated', 'Generado por Sistema')}
          render={(record: TagType) => (
            <Tooltip 
              title={
                record.isSystemGenerated 
                  ? translate('resources.tag-types.tooltips.system_generated', 'El sistema puede generar códigos automáticamente') 
                  : translate('resources.tag-types.tooltips.manual_only', 'Solo entrada manual')
              }
            >
              {record.isSystemGenerated ? (<CheckCircle fontSize="small" color="success" />) : (<Cancel fontSize="small" color="disabled" />)}
            
            </Tooltip>
          )}
          sortable={false}
        />
        
        {/* Manual Input Required */}
        <FunctionField
          label={translate('resources.tag-types.fields.requiresManualInput', 'Entrada Manual')}
          render={(record: TagType) => (
            <Tooltip 
              title={
                record.requiresManualInput 
                  ? translate('resources.tag-types.tooltips.requires_manual', 'Requiere entrada manual del usuario') 
                  : translate('resources.tag-types.tooltips.automatic', 'Generación automática')
              }
            >
              {record.requiresManualInput ? (
                <CheckCircle fontSize="small" color="success" />
              ) : (
                <Cancel fontSize="small" color="disabled" />
              )}
            </Tooltip>
          )}
          sortable={false}
        />
        
        {/* Created At */}
        <DateField 
          source="createdAt" 
          label={translate('resources.tag-types.fields.createdAt', 'Creado')} 
          showTime 
        />
      </CustomDataGrid>
    </List>
  );
};

export default TagTypeList;